import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useActorRef, useSelector } from '@xstate/react';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ActorRefFrom } from 'xstate';
import { getAccessToken } from '../api/auth-tokens.js';
import { useTypingStore } from '../features/live-chat/typing.store.js';
import { agentSnapshotQueryOptions } from '../features/presence/api.js';
import { createEventApplier } from '../realtime/apply-event.js';
import type { SocketFactory } from '../realtime/client.js';
import {
  type ConnectionStatus,
  connectionMachine,
  statusOf,
} from '../realtime/connection.machine.js';
import { chatTopic, type Topic } from '../realtime/frames.js';
import { gatewayUrl } from '../realtime/gateway-url.js';
import { SequenceTracker } from '../realtime/sequences.js';
import { topicsFor } from '../realtime/topics.js';
import { type AgentSessionSnapshot, agentSessionMachine } from './agent.machine.js';
import { holdsPermission } from './permissions.js';
import { createSnapshotReconciler } from './reconcile.js';
import { startRefreshing } from './session.js';
import { useSessionStore } from './session.store.js';
import { useCurrentUser } from './use-current-user.js';

interface SessionContextValue {
  readonly connection: ActorRefFrom<typeof connectionMachine>;
  readonly agentSession: ActorRefFrom<typeof agentSessionMachine>;
  /** Follows an interaction's stream, which is what a screen pop does when a call arrives. */
  readonly follow: (interactionId: string) => void;
  readonly unfollow: (interactionId: string) => void;
  /**
   * Replaces the topics a *screen* is watching, as opposed to the ones the session always watches.
   *
   * A dashboard follows `queue:<id>` and `team:<id>` while it is on screen and stops when it is not,
   * because a supervisor who has navigated away is a socket carrying events nobody will read. The
   * screen says which topics it wants and this is the whole of what it has to know: the session owns
   * the socket, and the gateway authorises every topic either way.
   */
  readonly watchTopics: (topics: readonly Topic[]) => void;
  /**
   * Follows a chat's own topic, which an interaction's does not cover.
   *
   * A chat's messages arrive on `interaction:<id>` as well, because its events order behind the
   * interaction — but **typing** does not: the gateway relays a typing frame to the other
   * subscribers of the `chat:` topic and to nobody else. So a pane that wants to see the visitor
   * typing, or to tell them it is typing back, has to hold the topic itself.
   *
   * Additive, like `follow`, rather than replacing like `watchTopics`: an agent may have three
   * chats open, and each pane knows only its own.
   */
  readonly followChat: (sessionId: string) => void;
  readonly unfollowChat: (sessionId: string) => void;
  /** Says this agent is typing, or has stopped, on a chat they hold. */
  readonly sendTyping: (sessionId: string, typing: boolean) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export interface SessionProviderProps {
  readonly children: ReactNode;
  /** A fake socket and a fake gateway address, for the tests that drive a reconnection. */
  readonly socket?: SocketFactory;
  readonly url?: string;
  readonly heartbeatMs?: number;
  readonly random?: () => number;
}

/**
 * The signed-in session: the token's lifetime, the agent's own statechart, and the socket.
 *
 * The three belong together because each one is the reason the next exists: a session has a token,
 * the token opens a socket, and the socket is what moves the agent's chart. Splitting them into
 * three providers would mean three places that have to agree on when a session has ended.
 *
 * It is mounted inside the router's shell rather than around the application, so a sign-in page has
 * no socket and no agent chart to speak of — there is nothing to connect as yet.
 */
export function SessionProvider({
  children,
  socket,
  url,
  heartbeatMs,
  random,
}: SessionProviderProps) {
  const queryClient = useQueryClient();
  const { data: me } = useCurrentUser();
  const status = useSessionStore((state) => state.status);
  const expiresAt = useSessionStore((state) => state.accessTokenExpiresAt);
  const deviceReady = useSessionStore((state) => state.deviceReady);
  const [followed, setFollowed] = useState<readonly string[]>([]);
  /** The chats whose own topics are being held, by session id. */
  const [chats, setChats] = useState<readonly string[]>([]);
  /** The topics whatever is on screen is watching. Empty for every screen that watches none. */
  const [watched, setWatched] = useState<readonly Topic[]>([]);

  // One tracker for the whole session: it is what `subscribe` and `resync` are built from, so it
  // must outlive every socket the session opens.
  const sequences = useRef(new SequenceTracker()).current;
  // Read through a ref so the socket and the heartbeat always see the current values without the
  // chart being rebuilt — rebuilding it would drop the socket on every token refresh.
  const deviceReadyRef = useRef(deviceReady);
  deviceReadyRef.current = deviceReady;

  const agentSession = useActorRef(agentSessionMachine);
  const isAgent = me !== undefined && holdsPermission(me, 'agents.activity');

  /**
   * The agent's own snapshot, and the chart that mirrors it.
   *
   * The `agent:<me>` topic carries `agent.activity_changed` and `agent.became_routable`, both
   * without a payload, so "something about you changed" is the whole of what the socket says and
   * reading this back is the whole of what the client does about it. The chart is fed from the
   * query rather than from the event, which means one path — snapshot in, state out — whether the
   * change arrived live, through a replay, or through reconciliation.
   */
  const agentSnapshot = useQuery({ ...agentSnapshotQueryOptions(), enabled: isAgent });

  useEffect(() => {
    if (agentSnapshot.data !== undefined) {
      agentSession.send({ type: 'snapshot', snapshot: agentSnapshot.data });
    }
  }, [agentSession, agentSnapshot.data]);

  // Read through a ref so the reconciler stays the same function for the chart's whole life: giving
  // the chart a new one would mean rebuilding it, and rebuilding it drops the socket.
  const isAgentRef = useRef(isAgent);
  isAgentRef.current = isAgent;

  // What a resync that could not be replayed falls back to.
  const reconcileSnapshots = useMemo(
    () =>
      createSnapshotReconciler({
        queryClient,
        agentSession,
        isAgent: () => isAgentRef.current,
      }),
    [agentSession, queryClient],
  );

  const applyEvent = useMemo(
    () => createEventApplier({ queryClient, agentSession }),
    [agentSession, queryClient],
  );

  /**
   * The visitor typing, straight into the store the panes read.
   *
   * Not through the query cache and not through the chart: it is a fact with a deadline and no
   * server behind it, and the one thing it must not do is make the workspace re-render on every
   * keystroke of every visitor. An agent's own typing is not shown back to them, so only the
   * visitor's half is kept.
   */
  const onPeerTyping = useMemo(
    () => (topic: string, from: 'visitor' | 'agent', typing: boolean) => {
      if (from === 'visitor') {
        useTypingStore.getState().peerTyping(topic, typing);
      }
    },
    [],
  );

  const connection = useActorRef(connectionMachine, {
    input: {
      url: url ?? gatewayUrl(),
      token: getAccessToken,
      deviceReady: () => deviceReadyRef.current,
      topics: [],
      sequences,
      applyEvent,
      onPeerTyping,
      reconcileSnapshots,
      socket,
      heartbeatMs,
      random,
    },
  });

  // The token is kept fresh for as long as there is a session. Recovering one is the root route's
  // job, before this ever renders: a gate that let the page render first would be no gate.
  // The gateway hears about a refresh as a control frame rather than as a reconnection, so a call
  // survives it.
  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }
    return startRefreshing(
      expiresAt,
      () => connection.send({ type: 'TOKEN_REFRESHED' }),
      () => connection.send({ type: 'STOP' }),
    );
  }, [connection, status, expiresAt]);

  // The socket opens once there is a session, and only once the permissions are known: the topics
  // to ask for come out of them.
  useEffect(() => {
    if (status !== 'authenticated' || me === undefined) {
      return;
    }
    connection.send({
      type: 'TOPICS',
      topics: [...topicsFor(me, followed), ...chats.map(chatTopic), ...watched],
    });
    connection.send({ type: 'START' });
  }, [chats, connection, followed, me, status, watched]);

  useEffect(() => {
    if (status === 'anonymous') {
      connection.send({ type: 'STOP' });
    }
  }, [connection, status]);

  const value = useMemo<SessionContextValue>(
    () => ({
      connection,
      agentSession,
      follow: (interactionId) =>
        setFollowed((current) =>
          current.includes(interactionId) ? current : [...current, interactionId],
        ),
      unfollow: (interactionId) => {
        sequences.forget(interactionId);
        setFollowed((current) => current.filter((id) => id !== interactionId));
      },
      followChat: (sessionId) =>
        setChats((current) => (current.includes(sessionId) ? current : [...current, sessionId])),
      unfollowChat: (sessionId) => {
        useTypingStore.getState().clear(chatTopic(sessionId));
        setChats((current) => current.filter((id) => id !== sessionId));
      },
      sendTyping: (sessionId, typing) =>
        connection.send({ type: 'TYPING', topic: chatTopic(sessionId), typing }),
      watchTopics: (topics) =>
        // Replaced only when it has actually changed: the effect above re-subscribes on every new
        // array, and a screen that rebuilt its list on each render would re-subscribe for ever.
        setWatched((current) => (sameTopics(current, topics) ? current : [...topics])),
    }),
    [agentSession, connection, sequences],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function useSessionContext(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === undefined) {
    throw new Error('this component must be rendered inside a SessionProvider');
  }
  return value;
}

/** What the workspace shows about its connection; the banner's only input. */
export function useConnectionStatus(): ConnectionStatus {
  return useSelector(useSessionContext().connection, statusOf);
}

/** The agent's own session, as a selector so a screen re-renders on what it asked for. */
export function useAgentSession<T>(select: (snapshot: AgentSessionSnapshot) => T): T {
  return useSelector(useSessionContext().agentSession, select);
}

/** Starts and stops following an interaction's stream. */
export function useInteractionStream(): Pick<SessionContextValue, 'follow' | 'unfollow'> {
  const { follow, unfollow } = useSessionContext();
  return { follow, unfollow };
}

/** A chat pane's half of the socket: its own topic, and the typing that only travels on it. */
export function useChatStream(): Pick<
  SessionContextValue,
  'followChat' | 'unfollowChat' | 'sendTyping'
> {
  const { followChat, unfollowChat, sendTyping } = useSessionContext();
  return { followChat, unfollowChat, sendTyping };
}

/** Replaces the topics the screen on display is watching. A dashboard's half of the socket. */
export function useWatchedTopics(): SessionContextValue['watchTopics'] {
  return useSessionContext().watchTopics;
}

/** Whether two topic lists are the same set, in order, which is all a re-subscription needs. */
function sameTopics(a: readonly Topic[], b: readonly Topic[]): boolean {
  return a.length === b.length && a.every((topic, index) => topic === b[index]);
}
